import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { configureAmplify } from './amplify-config';
import { restoreActiveZone } from './lib/zone-session';
import { installTabIdentityGuard } from './lib/tab-identity';
import { SowEmbedPage } from './pages/SowEmbedPage';
import { SignInEmbedPage } from './pages/SignInEmbedPage';
import './styles.css';

// Marks the document as the subscriber portal. Also gave the entry bundle a new content hash on
// 13 October 2026: the Cloudflare zone cache had stored the SPA-fallback HTML under the previous
// entry's URL for browser (Origin-bearing) requests, with the 1-year immutable header, so the app
// rendered blank. A new name is the only cache-bust available without zone access.
document.documentElement.dataset.portal = 'subscriber';

/**
 * ── WHY THE EMBED BRANCHES HERE, BEFORE THE PORTAL LOADS (17 July 2026) ──────
 *
 * The www sign-up/sign-in modal frames /embed/*, and its shell gives the embed
 * 6000ms (sow-modal.js LOAD_TIMEOUT_MS) to announce `nexa-sow:hello` — the
 * embed's "my code ran, so the frame rendered" signal, posted once React mounts.
 * Miss it and the customer is shown a grey "Something got in the way of the
 * secure form" panel instead of the form.
 *
 * `hello` therefore cannot fire until this entry chunk has DOWNLOADED, PARSED
 * and MOUNTED. While main.tsx imported App eagerly, that chunk was the WHOLE
 * subscriber portal — 27 pages, every provider, the navbar layer — so a sign-up
 * form dragged 1252KB of JS + 315KB of CSS across the wire before it could say a
 * word. Measured against live www on slow 4G, `hello` landed at ~3.2s: inside
 * the budget, but only 1.9x clear of it; 3G measured 9.3s — a grey box for a
 * real customer on a real phone. The failure is invisible to curl and to a
 * datacentre-speed CI runner, which is why it went unnoticed for so long
 * (the embed-probe Lambda's lambdas/embed-probe/src/check-render.ts now
 * throttles precisely to catch it).
 *
 * Splitting the portal behind React.lazy takes the embed's entry payload to
 * 317KB JS (382KB → 97KB gzip) and 38KB CSS — ~76% less to transfer and parse
 * before `hello`. The portal is unchanged in behaviour; it just arrives as its
 * own chunk (portal-shell.tsx), one extra request that is immutable-cached.
 *
 * ⚠ THE EMBED ROUTES LIVE HERE, NOT IN App.tsx — deliberately the only routes
 * this branch knows. Declaring them in App as well would put the whole portal
 * back in front of the embed and silently undo the split. App.tsx points here.
 * Keep this list in lockstep with the /embed/* rules in public/_headers and with
 * EMBED_ALLOWED_PARENT_ORIGINS in lib/sow-embed-bridge.ts.
 */
const isEmbed = window.location.pathname.startsWith('/embed/');

// The portal — everything that is not a chromeless embed. Lazily imported so its
// weight is NOT on the embed's critical path to `hello`.
const PortalShell = lazy(() => import('./portal-shell'));

// ⭐ ADR-120 Amendment 4 — RESTORE BEFORE CONFIGURE, AND THE ORDER IS THE WHOLE POINT.
// Amplify's tokens survive a reload in a host-scoped cookie (ADR-092 D4); its CONFIG does
// not. Without this line, a customer whose zone is not the one this bundle was built for
// would have their valid tokens validated against the wrong pool on every refresh and be
// silently signed out. `restoreActiveZone()` is a synchronous localStorage read that falls
// back to the build zone on absence or corruption, so the default path is unchanged.
// Configure Amplify before any render — Auth.* APIs are unusable until this runs.
// BOTH branches need it: /embed/sign-in authenticates from inside the frame.
configureAmplify(restoreActiveZone());
// Q13 — an open portal tab follows the person signed in to this browser (lib/tab-identity.ts).
// Not in the embed: a framed sign-in has no screens of its own to leave.
if (!isEmbed) installTabIdentityGuard();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}
createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      {isEmbed ? (
        <Routes>
          <Route path="/embed/sow" element={<SowEmbedPage />} />
          <Route path="/embed/sign-in" element={<SignInEmbedPage />} />
        </Routes>
      ) : (
        // fallback: null — index.html renders a bare #root, so this is the same
        // blank the portal already showed while its single bundle parsed; it is
        // not a new gap. The portal's real loading indicators live inside the
        // pages (the measured <2000ms data-ready standard), not on this boundary.
        <Suspense fallback={null}>
          <PortalShell />
        </Suspense>
      )}
    </BrowserRouter>
  </StrictMode>,
);
